import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Flag, RotateCw, Replace, Download, Loader2 } from "lucide-react";
import { API } from "../../context/AuthContext";
import { uploadFile } from "../../lib/uploadFile";

const btn = "flex items-center gap-1.5 text-[11px] font-mono font-bold px-3 py-1.5 rounded-lg border transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed";

export default function ReviewBar({ doc, caseId, canEdit, onDone, onReplaced }) {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const replaceRef = useRef(null);
  const s = doc.status;

  const act = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/documents/${doc.document_id}/review`, { action, reason });
      toast.success(action === "approve" ? "Document approved — its timeline events are now verified." : action === "reject" ? "Document rejected." : "Exception recorded.");
      setMode(null);
      setReason("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review action failed");
    } finally {
      setBusy(false);
    }
  };

  const reprocess = async () => {
    if (s === "approved" && !window.confirm("This document is approved. Reprocessing creates a new extraction that needs review again (attorney-approved deadlines and reviewer-assigned dates are kept). Continue?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/documents/${doc.document_id}/reprocess`);
      toast.success("Reprocessing with the current template version — previous results are kept as superseded.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reprocess");
    } finally {
      setBusy(false);
    }
  };

  const replace = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(file, { caseId, replacesDocumentId: doc.document_id });
      toast.success(`Uploaded v${(doc.version || 1) + 1} — the old version is superseded once processing finishes.`);
      onReplaced();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replacement upload failed");
    } finally {
      setBusy(false);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const download = async () => {
    try {
      const r = await axios.get(`${API}/files/${doc.file_id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch {
      toast.error("Original file unavailable");
    }
  };

  const locked = ["processing", "pending", "duplicate", "superseded", "withdrawn"].includes(s);
  return (
    <div className="space-y-2" data-testid="review-bar">
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <>
            <button disabled={busy || !["needs_review", "rejected"].includes(s)} onClick={() => act("approve")} data-testid="doc-approve-btn" className={`${btn} text-gray-950 bg-emerald-400 border-emerald-400 hover:bg-emerald-300`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} APPROVE
            </button>
            <button disabled={busy || !["needs_review", "approved"].includes(s)} onClick={() => setMode("reject")} data-testid="doc-reject-btn" className={`${btn} text-rose-300 border-rose-500/40 hover:bg-rose-500/10`}><X className="w-3 h-3" /> REJECT</button>
            <button disabled={busy || !["failed", "needs_review", "rejected"].includes(s)} onClick={() => setMode("exception")} data-testid="doc-exception-btn" className={`${btn} text-purple-300 border-purple-500/40 hover:bg-purple-500/10`}><Flag className="w-3 h-3" /> EXCEPTION</button>
            <button disabled={busy || locked} onClick={reprocess} data-testid="doc-reprocess-btn" className={`${btn} text-sky-300 border-sky-500/40 hover:bg-sky-500/10`}><RotateCw className="w-3 h-3" /> {s === "failed" ? "RETRY" : "REPROCESS"}</button>
            <button disabled={busy || locked} onClick={() => replaceRef.current?.click()} data-testid="doc-replace-btn" className={`${btn} text-amber-300 border-amber-500/40 hover:bg-amber-500/10`}><Replace className="w-3 h-3" /> REPLACE</button>
            <input ref={replaceRef} type="file" hidden onChange={(e) => replace(e.target.files?.[0])} data-testid="doc-replace-input" />
          </>
        )}
        <button onClick={download} data-testid="doc-download-btn" className={`${btn} text-gray-300 border-white/15 hover:text-white`}><Download className="w-3 h-3" /> ORIGINAL</button>
      </div>
      {mode && (
        <div className="flex flex-col sm:flex-row gap-2" data-testid="review-reason-box">
          <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason-input"
            placeholder={mode === "reject" ? "Why is this extraction wrong?" : "Document the exception (e.g. unreadable scan, unsupported format)"}
            className="flex-1 bg-gray-950 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#f59e0b]/50" />
          <button disabled={busy || reason.trim().length < (mode === "reject" ? 3 : 5)} onClick={() => act(mode)} data-testid="review-reason-submit" className={`${btn} text-gray-950 bg-[#f59e0b] border-[#f59e0b]`}>{mode === "reject" ? "CONFIRM REJECT" : "RECORD EXCEPTION"}</button>
          <button onClick={() => { setMode(null); setReason(""); }} data-testid="review-reason-cancel" className={`${btn} text-gray-400 border-white/10`}>CANCEL</button>
        </div>
      )}
    </div>
  );
}
